import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Edit3, Save, X } from 'lucide-react';
import ContentEditor from './ContentEditor';
import { PrincipleAnswersEditorProps } from '../../types/components';
import { PRINCIPLE_TYPES } from '../../types/problemDiagnosis';

const PrincipleAnswersEditor: React.FC<PrincipleAnswersEditorProps> = ({
  answers,
  onChange,
  isEditing,
  onEditToggle,
  onSave,
  onCancel,
  onAutoSave
}) => {
  const [expandedAnswers, setExpandedAnswers] = useState<Set<string>>(new Set());
  const [editingAnswerId, setEditingAnswerId] = useState<string | null>(null);

  const toggleExpanded = (principleType: string) => {
    const newExpanded = new Set(expandedAnswers);
    if (newExpanded.has(principleType)) {
      newExpanded.delete(principleType);
    } else {
      newExpanded.add(principleType);
    }
    setExpandedAnswers(newExpanded);
  };

  const handleAnswerChange = (principleType: string, newValue: string) => {
    const updatedAnswers = answers.map(answer => 
      answer.principle_type === principleType 
        ? { ...answer, user_answer: newValue }
        : answer
    );
    onChange(updatedAnswers);
  };

  const handleAnswerAutoSave = (principleType: string, value: string) => {
    handleAnswerChange(principleType, value);
    onAutoSave(answers);
  };

  const getPrincipleName = (principleType: string) => {
    const principleNames = {
      materiality: '物质性 (构成要素是什么？)',
      structure: '结构性 (它们如何组织？)',
      relationship: '关系性 (它们如何互动？)',
      dynamics: '动态性 (它如何演变？)',
      systemic: '系统性 (整体涌现出什么？)'
    };
    return principleNames[principleType as keyof typeof principleNames] || principleType;
  };

  const getPrincipleDescription = (principleType: string) => {
    const descriptions = {
      materiality: '分析问题涉及的关键要素、资源、人员等物质基础',
      structure: '分析这些要素之间的组织结构、层次关系和配置方式',
      relationship: '分析要素间的相互作用、依赖关系和影响模式',
      dynamics: '分析系统随时间的变化趋势、演进规律和驱动因素',
      systemic: '分析整体系统涌现的特性、文化氛围和综合效应'
    };
    return descriptions[principleType as keyof typeof descriptions] || '';
  };

  // 按顺序排列答案
  const sortedAnswers = [...answers].sort((a, b) => a.answer_order - b.answer_order);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">五大原理分析</h2>
          <p className="text-sm text-gray-600 mt-1">
            基于系统思维的五个维度深入分析问题
          </p>
        </div>
        
        {!isEditing && (
          <button
            onClick={onEditToggle}
            className="flex items-center space-x-2 px-3 py-2 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <Edit3 className="w-4 h-4" />
            <span>编辑回答</span>
          </button>
        )}
      </div>

      <div className="space-y-4">
        {sortedAnswers.map((answer, index) => {
          const isExpanded = expandedAnswers.has(answer.principle_type);
          const isCurrentlyEditing = isEditing && editingAnswerId === answer.principle_type;
          
          return (
            <div key={answer.principle_type} className="border border-gray-200 rounded-lg">
              {/* 原理标题 */}
              <div 
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleExpanded(answer.principle_type)}
              >
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900 mb-1">
                    {index + 1}. {getPrincipleName(answer.principle_type)}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {getPrincipleDescription(answer.principle_type)}
                  </p>
                </div>
                
                <div className="flex items-center space-x-2">
                  {/* 回答状态指示 */}
                  <div className={`w-2 h-2 rounded-full ${
                    answer.user_answer.trim() 
                      ? 'bg-green-500' 
                      : 'bg-gray-300'
                  }`} />
                  
                  {/* 展开/收起按钮 */}
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>

              {/* 回答内容 */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-100">
                  <div className="mt-4">
                    {isEditing ? (
                      <ContentEditor
                        value={answer.user_answer}
                        onChange={(value) => handleAnswerChange(answer.principle_type, value)}
                        placeholder={`请从${PRINCIPLE_TYPES[answer.principle_type as keyof typeof PRINCIPLE_TYPES]}的角度分析这个问题...`}
                        isEditing={true}
                        onEditToggle={() => {}}
                        onSave={() => {}}
                        onCancel={() => {}}
                        autoSave={true}
                        onAutoSave={(value) => handleAnswerAutoSave(answer.principle_type, value)}
                        maxLength={1500}
                        className="w-full"
                      />
                    ) : (
                      <div className="bg-gray-50 rounded-lg p-4">
                        {answer.user_answer ? (
                          <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                            {answer.user_answer}
                          </p>
                        ) : (
                          <p className="text-gray-500 italic">
                            暂未回答此原理的分析
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 编辑模式的操作按钮 */}
      {isEditing && (
        <div className="flex items-center justify-end mt-6 pt-4 border-t border-gray-200">
          <div className="flex items-center space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              <X className="w-4 h-4 inline mr-1" />
              取消编辑
            </button>
            
            <button
              onClick={onSave}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1"
            >
              <Save className="w-4 h-4" />
              <span>保存修改</span>
            </button>
          </div>
        </div>
      )}

      {/* 完成度统计 */}
      {!isEditing && (
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-gray-600">
              完成度：{answers.filter(a => a.user_answer.trim()).length}/{answers.length} 个原理已分析
            </div>
            
            <div className="flex items-center space-x-2">
              {answers.filter(a => a.user_answer.trim()).length === answers.length ? (
                <span className="text-sm text-green-600 font-medium">✓ 分析完整</span>
              ) : (
                <span className="text-sm text-orange-600 font-medium">
                  还有 {answers.length - answers.filter(a => a.user_answer.trim()).length} 个原理待分析
                </span>
              )}
            </div>
          </div>
          
          {/* 进度条 */}
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div 
              className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
              style={{ 
                width: `${(answers.filter(a => a.user_answer.trim()).length / answers.length) * 100}%` 
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PrincipleAnswersEditor;